import type { StaffNotification, StaffNotificationType } from '@/api/types';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  STOCK NOISE IS ONE ROW, NOT SIX
 * ════════════════════════════════════════════════════════════════════════════
 *
 * The inventory sweep files one LOT_EXPIRING notice per lot and one LOW_STOCK
 * per material. After a delivery of a dozen short-dated lots the bell's eight
 * preview rows were all «… تا N روز دیگه منقضی می‌شه», and the booking
 * request that actually needed an answer sat ninth, out of sight.
 *
 * So the preview folds every notice of a stock type into ONE row — «۶ هشدار
 * انقضای کالا» — placed where the newest of them was, and leading to the
 * inventory alerts tab, where each lot can be dealt with. Opening it marks the
 * folded notices read. A single notice of a type stays a normal row: folding
 * one item only hides its text. The full list («همهٔ اعلان‌ها») is never
 * folded; it is where each notice can still be read on its own.
 */

export interface NotificationGroup {
  type: StaffNotificationType;
  items: StaffNotification[];
  /** The Persian row title, count included by the renderer. */
  noun: string;
  link: string;
  typeLabelFa: string;
  isRead: boolean;
  createdAt: string;
}

export type NotificationEntry =
  { kind: 'single'; item: StaffNotification } | { kind: 'group'; group: NotificationGroup };

const FOLDABLE: Partial<Record<StaffNotificationType, { noun: string; link: string }>> = {
  LOT_EXPIRING: { noun: 'هشدار انقضای کالا', link: '/inventory?tab=alerts' },
  LOW_STOCK: { noun: 'کالای رو به اتمام', link: '/inventory?tab=alerts' },
};

export function groupNotifications(items: readonly StaffNotification[]): NotificationEntry[] {
  const byType = new Map<StaffNotificationType, StaffNotification[]>();
  for (const item of items) {
    if (!FOLDABLE[item.type]) continue;
    byType.set(item.type, [...(byType.get(item.type) ?? []), item]);
  }

  const emitted = new Set<StaffNotificationType>();
  const entries: NotificationEntry[] = [];
  for (const item of items) {
    const folded = byType.get(item.type);
    const spec = FOLDABLE[item.type];
    if (!spec || !folded || folded.length < 2) {
      entries.push({ kind: 'single', item });
      continue;
    }
    if (emitted.has(item.type)) continue;
    emitted.add(item.type);
    entries.push({
      kind: 'group',
      group: {
        type: item.type,
        items: folded,
        noun: spec.noun,
        link: spec.link,
        typeLabelFa: item.typeLabelFa,
        isRead: folded.every((entry) => entry.isRead),
        // The list is newest-first, so the first of the type is the newest.
        createdAt: item.createdAt,
      },
    });
  }
  return entries;
}
